/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertFromRaw,
  convertToRaw,
} from "draft-js";
import { getHtmlFromDraft } from "./helper.js";

export const getProjectDescription = (
  currentLanguage,
  originalData, //m.description
  lngArr, //support lng
  type, // object or string,
  processedEditorState, // bool
) => {
  if (!EditorState) return null;
  if (!originalData) return EditorState.createEmpty();
  if (typeof processedEditorState !== "boolean") return null;
  const processContent = (oData) => {
    if (processedEditorState) {
      return oData;
    }
    try {
      const obj = convertFromRaw(JSON.parse(oData));
      if (obj) {
        return EditorState.createWithContent(obj);
      } else {
        return EditorState.createEmpty();
      }
    } catch (e) {
      try {
        //for old data
        const htmlSting = getHtmlFromDraft(oData);
        const contentBlock = convertFromHTML(htmlSting);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock);
          return EditorState.createWithContent(contentState);
        }
      } catch (e) {
        return EditorState.createEmpty();
      }
    }
  };

  const getJsonData = (jsonData) => {
    let lngObj = {};

    if (typeof jsonData.isMultiLanguage === "boolean") {
      lngObj.isMultiLanguage = jsonData.isMultiLanguage;
    }

    for (var i = 0; i < lngArr.length; i++) {
      if (typeof jsonData[lngArr[i]] === "string")
        lngObj[lngArr[i]] = processContent(jsonData[lngArr[i]]);
      else lngObj[lngArr[i]] = processContent(jsonData.default);
    }
    return lngObj;
  };

  if (typeof originalData === "object") {
    if (typeof originalData.isMultiLanguage === "boolean") {
      try {
        if (type === "string") {
          if (originalData.isMultiLanguage) {
            // Deduplicated containers (CP-234) only store languages that
            // differ from default, so a missing language means "same as
            // default", not "empty".
            return processContent(
              typeof originalData[currentLanguage] !== "undefined"
                ? originalData[currentLanguage]
                : originalData.default
            );
          }
          const defaultData = processContent(originalData.default);
          return defaultData;
        } else {
          return getJsonData(originalData);
        }
      } catch (e) {
        return EditorState.createEmpty();
      }
    }
    try {
      return processContent(originalData);
    } catch (e) {
      return EditorState.createEmpty();
    }
  }
  try {
    const contentJson = JSON.parse(originalData);
    if (typeof contentJson.isMultiLanguage === "boolean") {
      return getJsonData(contentJson);
    } else {
      return processContent(originalData);
    }
  } catch (e) {
    return processContent(originalData);
  }
};

// getProjectDescription("object", ...) returns a bare EditorState (not the
// {isMultiLanguage, zh, en, cn, default} container) whenever the persisted
// description is legacy single-language content, or there's none yet — the
// common case for projects created before multi-language existed.
// Normalize into the container shape so callers always get one EditorState
// per lngArr entry, mirroring CreateGoal.js's own toggle-into-multi-language
// behavior (clone the existing content into every language slot).
export const normalizeDescriptionContent = (currentLanguage, originalData, lngArr) => {
  const raw = getProjectDescription(
    currentLanguage,
    originalData,
    lngArr,
    "object",
    false
  );
  if (raw && typeof raw.getCurrentContent === "function") {
    const normalized = { isMultiLanguage: false };
    lngArr.forEach((lng) => {
      normalized[lng] = raw;
    });
    return normalized;
  }
  return raw;
};

// Serialize one EditorState into the "description code" the bulk importer
// accepts when a row's Description Format is "Formatted" — the same shape
// CreateGoal.js persists, so the importer stores it untouched. Returns "" for
// empty content, which callers treat as nothing worth copying.
export const descriptionCodeFrom = (editorState) => {
  if (!editorState || typeof editorState.getCurrentContent !== "function") {
    return "";
  }
  const content = editorState.getCurrentContent();
  if (!content.hasText()) return "";
  return JSON.stringify(convertToRaw(content));
};

// Normalize an already-processed description — one whose per-language values
// are EditorStates, not persisted JSON strings — into the
// {isMultiLanguage, zh, en, cn, default} container shape. getProjectDescription
// (and so normalizeDescriptionContent) returns a bare EditorState for legacy
// single-language content and for empty descriptions, which is the shape trap
// documented above; this is the same fix for callers holding live editor state
// rather than persisted data. Clones into every slot, mirroring what toggling
// multi-language on already does.
export const toDescriptionContainer = (description, lngArr) => {
  if (description && typeof description.getCurrentContent === "function") {
    const container = {};
    lngArr.forEach((lng) => {
      container[lng] = description;
    });
    return container;
  }
  return description;
};

// Serialize a per-language map of description codes into the persisted
// {isMultiLanguage, ...languages, default} container string, dropping every
// language whose code is byte-identical to default (CP-234). Editors clone
// the default content into all four slots, so without this a single-language
// description is stored four times over (measured 79× inflation on a real
// document). Safe for readers: every read path falls back to `default` for a
// missing language, so a deduplicated container reads back identically.
// `isMultiLanguage` is stored as given (CreateGoal's toggle is user state,
// kept even with no distinct translation yet); when omitted it's derived
// from whether any distinct translation survived.
export const serializeDescriptionCodes = (codes, isMultiLanguage) => {
  const distinct = {};
  let hasDistinct = false;
  Object.keys(codes).forEach((lng) => {
    if (lng === "default" || typeof codes[lng] !== "string") return;
    if (codes[lng] !== codes.default) {
      distinct[lng] = codes[lng];
      hasDistinct = true;
    }
  });
  return JSON.stringify({
    isMultiLanguage:
      typeof isMultiLanguage === "boolean" ? isMultiLanguage : hasDistinct,
    ...distinct,
    default: codes.default,
  });
};

// Order of the bulk-import template's Description columns — Default, EN, ZH,
// CN — which is NOT the editor's own language order (config.js's
// DEFAULT_LANGUAGE_ARRAY starts with zh, and "default" is appended last).
// Kept in this order so one tab-separated paste lands each language in the
// right column; a test asserts it still matches buildProjectHeaders().
export const DESCRIPTION_CODE_COLUMN_ORDER = ["default", "en", "zh", "cn"];

// One description code per template Description column, in that column order.
// A language with no content becomes an empty field, which pastes as a blank
// cell — exactly how the importer is told to omit that language. Returns null
// when the required Default column has no content.
export const descriptionCodeFields = (content) => {
  if (!content) return null;
  const fields = DESCRIPTION_CODE_COLUMN_ORDER.map((lng) =>
    descriptionCodeFrom(content[lng])
  );
  return fields[0] ? fields : null;
};

// Tab-separated form of the above. This is the fallback clipboard flavour:
// spreadsheets are supposed to split pasted plain text on tabs, but Excel does
// not do it reliably for these payloads, so copyDescriptionCells below leads
// with an HTML table instead. Still safe by construction — JSON.stringify
// escapes tabs and newlines inside strings, so no field can contain a raw
// delimiter. Returns "" when the required Default column has no content.
export const descriptionCodesTsv = (content) => {
  const fields = descriptionCodeFields(content);
  return fields ? fields.join("\t") : "";
};

const escapeHtml = (str) =>
  String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Copy a row of cells to the clipboard for pasting across adjacent spreadsheet
// columns. Writes two flavours: an HTML table, which Excel and Sheets prefer
// and which carries cell boundaries structurally, and tab-separated plain text
// for anything that only takes text. The HTML flavour is what makes this
// reliable — plain-text TSV alone lands in a single cell in Excel.
//
// Resolves true on success. Note that no clipboard flavour helps if the target
// cell is in edit mode: a spreadsheet in edit mode pastes literal text into
// that one cell by definition.
export const copyDescriptionCells = async (fields) => {
  if (!Array.isArray(fields) || fields.length === 0) return false;
  const text = fields.join("\t");
  const html =
    "<html><body><table><tr>" +
    fields.map((f) => `<td>${escapeHtml(f)}</td>`).join("") +
    "</tr></table></body></html>";
  try {
    if (
      typeof window !== "undefined" &&
      window.ClipboardItem &&
      navigator.clipboard &&
      navigator.clipboard.write
    ) {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return true;
    }
  } catch (e) {
    // Fall through to the text-only path below.
  }
  return copyToClipboard(text);
};

// Copy text to the clipboard, resolving true on success. The clipboard API
// needs a secure context and can be absent or refuse, so callers should offer
// the text for manual selection when this resolves false.
export const copyToClipboard = async (text) => {
  try {
    if (!navigator.clipboard || !navigator.clipboard.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
};

// Plain-text preview of a project description for a given language, for
// list/table views that can't render full Draft.js rich text. Falls back to
// "default" for languages that aren't set, same as the editor.
export const getProjectDescriptionText = (currentLanguage, originalData, lngArr) => {
  if (!originalData) return "";
  const content = normalizeDescriptionContent(currentLanguage, originalData, lngArr);
  const editorState = content && (content[currentLanguage] || content.default);
  if (!editorState || typeof editorState.getCurrentContent !== "function") return "";
  return editorState.getCurrentContent().getPlainText();
};
